// SQL generation utilities for transformations

export function sanitizeColumnName(columnName: string): string {
  // Escape embedded double quotes by doubling them, then wrap in double quotes for DuckDB
  return `"${columnName.replace(/"/g, '""')}"`;
}

export function escapeStringLiteral(value: string): string {
  // Escape single quotes by doubling them
  return value.replace(/'/g, "''");
}

export function generateTypeCastSql(
  columnName: string,
  targetType: string,
  safeCast: boolean = false
): string {
  const castFunction = safeCast ? 'TRY_CAST' : 'CAST';
  return `${castFunction}(${sanitizeColumnName(columnName)} AS ${targetType})`;
}

export function generateStringFunctionSql(
  columnName: string,
  functionName: string,
  parameters: Record<string, any> = {}
): string {
  const col = sanitizeColumnName(columnName);

  switch (functionName.toUpperCase()) {
    case 'UPPER':
    case 'LOWER':
    case 'TRIM':
    case 'LTRIM':
    case 'RTRIM':
    case 'LENGTH':
      return `${functionName}(${col})`;

    case 'SUBSTRING':
      const start = parameters.start || 1;
      const length = parameters.length || 10;
      return `SUBSTRING(${col}, ${start}, ${length})`;

    case 'CONCAT':
      const value = escapeStringLiteral(parameters.value || '');
      return `CONCAT(${col}, '${value}')`;

    case 'REPLACE':
      const from = escapeStringLiteral(parameters.from || '');
      const to = escapeStringLiteral(parameters.to || '');
      return `REPLACE(${col}, '${from}', '${to}')`;

    case 'REGEXP_REPLACE':
      const pattern = escapeStringLiteral(parameters.pattern || '');
      const replacement = escapeStringLiteral(parameters.replacement || '');
      return `REGEXP_REPLACE(${col}, '${pattern}', '${replacement}')`;

    case 'LEFT':
    case 'RIGHT':
      const n = parameters.n || 5;
      return `${functionName}(${col}, ${n})`;

    default:
      return `${functionName}(${col})`;
  }
}

export function generateDateFunctionSql(
  columnName: string,
  functionName: string,
  parameters: Record<string, any> = {}
): string {
  const col = sanitizeColumnName(columnName);

  switch (functionName.toUpperCase()) {
    case 'DATE_TRUNC':
      const unit = parameters.unit || 'day';
      return `DATE_TRUNC('${unit}', ${col})`;

    case 'EXTRACT':
      const part = parameters.part || 'year';
      return `EXTRACT(${part} FROM ${col})`;

    case 'DATE_DIFF':
      const diffUnit = parameters.unit || 'day';
      const startDate = parameters.start_date ? sanitizeColumnName(parameters.start_date) : col;
      const endDate = parameters.end_date ? sanitizeColumnName(parameters.end_date) : 'CURRENT_DATE';
      return `DATE_DIFF('${diffUnit}', ${startDate}, ${endDate})`;

    case 'STRFTIME':
      const format = escapeStringLiteral(parameters.format || '%Y-%m-%d');
      return `STRFTIME(${col}, '${format}')`;

    case 'CURRENT_DATE':
      return 'CURRENT_DATE';

    case 'CURRENT_TIMESTAMP':
      return 'CURRENT_TIMESTAMP';

    default:
      return `${functionName}(${col})`;
  }
}

export function generateMathFunctionSql(
  columnName: string,
  functionName: string,
  parameters: Record<string, any> = {}
): string {
  const col = sanitizeColumnName(columnName);

  switch (functionName.toUpperCase()) {
    case 'ROUND':
      const decimals = parameters.decimals ?? 0;
      return `ROUND(${col}, ${decimals})`;

    case 'FLOOR':
    case 'CEIL':
    case 'ABS':
    case 'SQRT':
      return `${functionName}(${col})`;

    case 'POWER':
      const exponent = parameters.exponent || 2;
      return `POWER(${col}, ${exponent})`;

    case 'MOD':
      const divisor = parameters.divisor || 2;
      return `MOD(${col}, ${divisor})`;

    case 'ARITHMETIC':
      const operation = parameters.operation || 'multiply';
      const operand = parameters.operand || 1;
      const operators: Record<string, string> = {
        add: '+',
        subtract: '-',
        multiply: '*',
        divide: '/',
      };
      const operator = operators[operation] || '*';
      return `(${col} ${operator} ${operand})`;

    default:
      return `${functionName}(${col})`;
  }
}

export function generateNullHandlingSql(
  columnName: string,
  functionName: string,
  defaultValue: string
): string {
  const col = sanitizeColumnName(columnName);

  // Try to determine if default value is a string literal
  const isStringLiteral = defaultValue.includes("'") ||
                          (!defaultValue.match(/^-?\d+(\.\d+)?$/) &&
                           !defaultValue.toUpperCase().startsWith('NULL') &&
                           !defaultValue.includes('"'));

  const value = isStringLiteral && !defaultValue.includes("'")
    ? `'${escapeStringLiteral(defaultValue)}'`
    : defaultValue;

  switch (functionName.toUpperCase()) {
    case 'COALESCE':
    case 'IFNULL':
      return `${functionName}(${col}, ${value})`;

    case 'NULLIF':
      return `NULLIF(${col}, ${value})`;

    default:
      return `COALESCE(${col}, ${value})`;
  }
}

export interface CaseWhenCondition {
  condition: string;
  result: string;
}

export function generateCaseWhenSql(
  columnName: string,
  conditions: CaseWhenCondition[],
  elseResult?: string
): string {
  const col = sanitizeColumnName(columnName);

  let sql = 'CASE';

  for (const { condition, result } of conditions) {
    // Replace column reference in condition
    const processedCondition = condition.replace(
      new RegExp(`\\b${columnName}\\b`, 'g'), // nosemgrep: rules.eslint.detect-non-literal-regexp  -- SQL generator builds a word-boundary match from a sanitized column name
      col
    );

    // Determine if result is a string literal
    const isStringLiteral = !result.match(/^-?\d+(\.\d+)?$/) &&
                            !result.toUpperCase().startsWith('NULL') &&
                            !result.includes("'") &&
                            !result.includes('"');

    const processedResult = isStringLiteral
      ? `'${escapeStringLiteral(result)}'`
      : result;

    sql += `\n  WHEN ${processedCondition} THEN ${processedResult}`;
  }

  if (elseResult !== undefined && elseResult !== '') {
    const isStringLiteral = !elseResult.match(/^-?\d+(\.\d+)?$/) &&
                            !elseResult.toUpperCase().startsWith('NULL') &&
                            !elseResult.includes("'") &&
                            !elseResult.includes('"');

    const processedElse = isStringLiteral
      ? `'${escapeStringLiteral(elseResult)}'`
      : elseResult;

    sql += `\n  ELSE ${processedElse}`;
  }

  sql += '\nEND';

  return sql;
}

export interface JsonExtractionConfig {
  extractionMode: 'single_field' | 'nested_path';
  fieldName?: string;
  jsonPath?: string;
  returnType: string;
  safeCast: boolean;
}

export function generateJsonExtractionSql(
  columnName: string,
  config: JsonExtractionConfig
): string {
  const { extractionMode, fieldName, jsonPath, returnType, safeCast } = config;
  const col = sanitizeColumnName(columnName);

  let extraction: string;

  if (extractionMode === 'single_field') {
    if (!fieldName) return '';
    const escapedField = escapeStringLiteral(fieldName);
    extraction = `${col}->>'${escapedField}'`;
  } else {
    if (!jsonPath) return '';
    const escapedPath = escapeStringLiteral(jsonPath);
    extraction = `json_extract_string(${col}, '$.${escapedPath}')`;
  }

  if (returnType === 'VARCHAR') {
    return extraction;
  }

  const castFunction = safeCast ? 'TRY_CAST' : 'CAST';
  return `${castFunction}(${extraction} AS ${returnType})`;
}

export interface WindowFunctionConfig {
  functionName: string;
  partitionBy?: string[];
  orderBy?: string[];
  offset?: number;
  ascending?: boolean;
}

export function generateWindowFunctionSql(
  columnName: string,
  config: WindowFunctionConfig
): string {
  const { functionName, partitionBy = [], orderBy = [], offset = 1, ascending = true } = config;

  let sql = '';

  // Generate function call
  switch (functionName.toUpperCase()) {
    case 'ROW_NUMBER':
    case 'RANK':
    case 'DENSE_RANK':
      sql = `${functionName}()`;
      break;

    case 'LAG':
    case 'LEAD':
      sql = `${functionName}(${sanitizeColumnName(columnName)}${offset !== 1 ? `, ${offset}` : ''})`;
      break;

    case 'FIRST_VALUE':
    case 'LAST_VALUE':
      sql = `${functionName}(${sanitizeColumnName(columnName)})`;
      break;

    default:
      sql = `${functionName}(${sanitizeColumnName(columnName)})`;
  }

  // Add OVER clause
  sql += ' OVER (';

  const clauses: string[] = [];

  if (partitionBy.length > 0) {
    clauses.push(`PARTITION BY ${partitionBy.map(sanitizeColumnName).join(', ')}`);
  }

  if (orderBy.length > 0) {
    const orderClause = orderBy.map(col =>
      `${sanitizeColumnName(col)}${!ascending ? ' DESC' : ''}`
    ).join(', ');
    clauses.push(`ORDER BY ${orderClause}`);
  }

  sql += clauses.join(' ');
  sql += ')';

  return sql;
}
